import { forwardRef } from 'react';

import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import Button from '@liquidweb-dev/undercurrent/Button';
import CardLayeredFooter from '@liquidweb-dev/undercurrent/CardLayeredFooter';
import InfoIcon from '@liquidweb-dev/undercurrent/icons/lucide/Info';
import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

import HookedTextField from 'components/standard/Form/HookedTextField';
import useExtractCSRDetails from 'modules/queries/ssl/certificate/useExtractCSRDetails';
import Autorenew from './Autorenew';
import TypeCard from './TypeCard';
import VerificationMethod from './VerificationMethod';

export interface ManualMethodProps extends Omit<StackProps, 'children'> {
	autorenew: boolean;
	csr?: string;
}

const ManualMethod = forwardRef<HTMLDivElement, ManualMethodProps>(
	({ autorenew, csr, sx, ...rest }, ref) => {
		const { data: extractedCsrDetails, isError: extractedCsrIsError } =
			useExtractCSRDetails({ csr: csr || '' }, { enabled: Boolean(csr) });

		const {
			autoVerifyCapable: autoVerifyCapableRaw,
			wildcard: wildcardRaw,
			commonName,
			city,
			state,
			country,
			organization,
			organizationalUnit,
			emailAddress,
			san,
		} = extractedCsrDetails || {};

		const isAutoVerifyCapable = fromApiBoolean(autoVerifyCapableRaw);
		const wildcard = fromApiBoolean(wildcardRaw);

		return (
			<Stack sx={combineSx({ gap: 500 }, sx)} ref={ref} {...rest}>
				<Stack sx={{ gap: 300 }}>
					<TypeCard
						autorenew={autorenew}
						wildcard={wildcard}
						commonName={commonName}
						city={city}
						state={state}
						country={country}
						emailAddress={emailAddress}
						organization={organization}
						organizationalUnit={organizationalUnit}
						san={san}
					/>

					<Stack sx={{ flexDirection: { sm: 'row' }, gap: 200 }}>
						<VerificationMethod
							auto={isAutoVerifyCapable}
							dns={!isAutoVerifyCapable}
							metatag={!isAutoVerifyCapable}
							sx={{ flex: 1 }}
						/>
						<Autorenew sx={{ flex: 1 }} />
					</Stack>
				</Stack>

				<Stack
					sx={{
						// offset negative margin of layered footer
						paddingBottom: 200,
					}}
				>
					<HookedTextField
						multiline
						minRows={5}
						name="csr"
						label={
							<Stack
								sx={{
									flexDirection: { sm: 'row' },
									gap: 100,
									alignItems: { sm: 'center' },
									justifyContent: 'space-between',
								}}
							>
								<Stack sx={{ gap: 100 }}>
									<Typography variant="label-lg" sx={{ fontWeight: 500 }}>
										Certificate Signing Request (CSR)
									</Typography>
									<Typography
										variant="label-lg"
										sx={({ vars }) => ({
											color: vars.palette.uc.text.weak,
											fontWeight: 400,
											whiteSpace: 'normal',
										})}
									>
										All required info like domain and wildcard status is
										extracted from the CSR
									</Typography>
								</Stack>
								<Button
									variant="secondary"
									component="a"
									href="https://www.liquidweb.com/help-docs/security/ssl/"
									target="_blank"
								>
									Learn more
								</Button>
							</Stack>
						}
						placeholder="Paste CSR here"
						slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
					/>
					<CardLayeredFooter
						sx={({ vars }) => ({
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: 150,
							marginTop: -200,
							paddingTop: 300,
							paddingBottom: 100,
							backgroundColor: extractedCsrIsError
								? vars.palette.uc.danger.bg.main
								: undefined,
						})}
					>
						{extractedCsrIsError ? (
							<Typography variant="body-sm">
								We were not able to extract the details from your provided CSR.
							</Typography>
						) : (
							<>
								<InfoIcon
									sx={({ size }) => ({
										height: size.xS,
										width: size.xS,
										flexShrink: 0,
									})}
								/>
								<Typography variant="body-sm">
									To cover both a domain and subdomain, generate your CSR using
									that subdomain or use a wildcard
								</Typography>
							</>
						)}
					</CardLayeredFooter>
				</Stack>
			</Stack>
		);
	},
);

ManualMethod.displayName = 'ManualMethod';

export default ManualMethod;
